import React, { useRef, useEffect, useCallback } from 'react';
import { Box, useTheme } from '@mui/material';

interface VUMeterProps {
  analyser: AnalyserNode | null;
  isPlaying: boolean;
  width?: number;
  height?: number;
}

const VUMeter: React.FC<VUMeterProps> = ({ 
  analyser, 
  isPlaying,
  width = 200,
  height = 30
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const theme = useTheme();

  // Function to clear and reset the canvas
  const resetCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background
    ctx.fillStyle = theme.palette.mode === 'dark' 
      ? '#3a3a3a' 
      : '#e0e0e0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw level markers
    ctx.fillStyle = theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.3)' 
      : 'rgba(0, 0, 0, 0.2)';
    
    for (let i = 0; i <= 10; i++) {
      const x = (canvas.width * i) / 10;
      const markerHeight = i % 5 === 0 ? canvas.height * 0.4 : canvas.height * 0.2;
      ctx.fillRect(x, canvas.height - markerHeight, 1, markerHeight);
    }
    
    // Draw -∞ dB text when not playing
    if (!isPlaying && width > 100 && height > 20) {
      ctx.fillStyle = theme.palette.mode === 'dark' 
        ? '#ffffff' 
        : '#000000';
      ctx.font = '10px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('-∞ dB', canvas.width - 5, canvas.height - 5);
    }
  }, [theme, isPlaying, width, height]);

  // Effect to draw the VU meter when analyser or isPlaying changes
  useEffect(() => {
    // Cancel any existing animation frame
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // If not playing or no analyzer, reset and return
    if (!isPlaying || !analyser || !canvasRef.current) {
      resetCanvas();
      console.log('[VUMeter] useEffect cleanup/reset. isPlaying:', isPlaying, 'analyser:', !!analyser);
      return;
    }

    console.log('[VUMeter] useEffect running. Analyser:', analyser);
    console.log('[VUMeter] Analyser properties: fftSize', analyser.fftSize, 'frequencyBinCount', analyser.frequencyBinCount);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Animation function to draw the meter
    const draw = () => {
      // Get the audio data
      analyser.getByteTimeDomainData(dataArray);
      
      // Log a sample of the data
      if (dataArray.length > 0) {
        const sample = Array.from(dataArray.slice(0, 10)); // Get first 10 values
        // Check if all values are 128 (silence for PCM byte data)
        const isSilent = sample.every(val => val === 128);
        if (animationRef.current && animationRef.current % 60 === 0) { // Log roughly once per second
            console.log('[VUMeter] TimeDomainData sample (first 10):', sample, 'Is silent (all 128s)?', isSilent);
        }
      }
      
      // Calculate RMS (Root Mean Square) to get a more useful volume value
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const amplitude = (dataArray[i] - 128) / 128; // Convert to -1 to 1
        sum += amplitude * amplitude;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      
      // Calculate dB value (avoid log(0))
      const db = rms > 0 ? 20 * Math.log10(rms) : -100;
      
      // Normalize to 0-1 range for display (-60dB to 0dB)
      const normalized = Math.max(0, Math.min(1, (db + 60) / 60));
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw background
      ctx.fillStyle = theme.palette.mode === 'dark' 
        ? '#3a3a3a' 
        : '#e0e0e0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Create gradient from green to yellow to red
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, '#4CAF50');   // Green (Material UI green)
      gradient.addColorStop(0.6, '#FFC107'); // Yellow (Material UI amber)
      gradient.addColorStop(0.8, '#FF9800'); // Orange (Material UI orange)
      gradient.addColorStop(1, '#F44336');   // Red (Material UI red)
      
      // Draw meter
      const meterWidth = Math.max(1, canvas.width * normalized); // Ensure at least 1px width
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, meterWidth, canvas.height);
      
      // Draw peak indicator (red line)
      if (normalized > 0.9) {
        ctx.fillStyle = '#F44336';
        ctx.fillRect(canvas.width - 2, 0, 2, canvas.height);
      }
      
      // Draw level markers
      ctx.fillStyle = theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.3)' 
        : 'rgba(0, 0, 0, 0.2)';
      
      for (let i = 0; i <= 10; i++) {
        const x = (canvas.width * i) / 10;
        const markerHeight = i % 5 === 0 ? canvas.height * 0.4 : canvas.height * 0.2;
        ctx.fillRect(x, canvas.height - markerHeight, 1, markerHeight);
      }
      
      // Draw dB value text if the meter is large enough
      if (width > 100 && height > 20) {
        ctx.fillStyle = theme.palette.mode === 'dark' 
          ? '#ffffff' 
          : '#000000';
        ctx.font = '10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(`${Math.round(db)}dB`, canvas.width - 5, canvas.height - 5);
      }
      
      // Continue animation loop
      if (isPlaying) {
        animationRef.current = requestAnimationFrame(draw);
      }
    };

    // Start the animation loop
    animationRef.current = requestAnimationFrame(draw);

    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      resetCanvas();
    };
  }, [analyser, isPlaying, theme.palette.mode, width, height, resetCanvas]);

  // Effect for initial canvas setup and reset on analyser/playing change
  useEffect(() => {
    resetCanvas();
    // Cleanup for this effect should ensure the canvas is reset if analyser/isPlaying changes again
    return () => {
      resetCanvas(); 
    };
  }, [analyser, isPlaying, resetCanvas]);

  return (
    <Box sx={{ 
      width: width, 
      height: height, 
      bgcolor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0',
      borderRadius: '4px',
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`
    }}>
      <canvas 
        ref={canvasRef} 
        width={width} 
        height={height} 
        style={{ width: '100%', height: '100%' }}
      />
    </Box>
  );
};

export default VUMeter; 